import CustomDataSourceFields from '../../../../types/report/CustomDataSourceFields';
import FunctionOperand from './FunctionOperand';
import IngestionConfig from '../../../../types/ingestion/IngestionConfig';
import React from 'react';
import operandType from '../../../../infrastructure/constants/functions/operandType';
import typeHelper from '../../../../infrastructure/helpers/common/typeHelper';
import operandDisplayHelper from '../../../../infrastructure/helpers/functions/operand/operandDisplayHelper';
import Lookup from '../../../../types/functions/Lookup';
import Operand from '../../../../types/functions/Operand/Operand';
import OperandDetails from '../../../../types/functions/Operand/OperandDetails';
import Dictionary from '../../../../types/report/Dictionary';
import ReportFieldDefinition from '../../../../types/report/ReportFieldDefinition';
import ReportVariable from '../../../../types/report/ReportVariable';
import getDictionaryOperand from './getDictionaryOperand';
import UserDefinedFunction from '../../../../types/report/UserDefinedFunction';
import getUserDefinedFunctionOperand from './getUserDefinedFunctionOperand';
import ReportLogicLocation from '../../../../types/functions/Location/ReportLogicLocation';
import ReportCase from '../../../../types/report/ReportCase';
import locationType from '../../../../types/functions/Location/LocationType';
import Aggregation from '../../../../types/report/Aggregation';
import mixedOperandHelper from '../../../../infrastructure/helpers/functions/operand/mixedOperandHelper';
import actions from '../../../../store/actions';
import store from '../../../../store/store';
import LookupMethod from '../../../../types/report/LookupMethod';
import IndexedLookupArgumentsState from '../../Arguments/IndexedLookupArgumentsState';

const getOperandButtonText = (operand: OperandDetails): string => {
    if (!operand.isSetUp) {
        return '<not set>';
    }

    return operand.displayText;
};

const getOperandButtonClasses = (operand: OperandDetails): string => {
    let classes = ['transparent-button', 'code'];

    if (operand.isSetUp) {
        classes.push(operand.operandType.toLowerCase());
    }
    else {
        classes.push('instruction');
    }

    return classes.join(' ');
};

const onShowDefinitionClick = (details: OperandDetails, location: ReportLogicLocation): void => {

    store.dispatch({ type: actions.navigation.navigateToItem, payload: { details, location } });
}

const getDisplayName = (operand: Operand, aggregationName: string, dataSources: IngestionConfig[], variables: ReportVariable[], details: OperandDetails): string => {

    let name = '';

    if (operand.operandType === operandType.field) {

        let field = operandDisplayHelper.getFieldDetails(operand, dataSources)?.field;
        name = field?.name || '';
    }
    else if (operand.operandType === operandType.variable) {

        let variable = variables.find(v => v.number === operand.variableNumber);
        name = variable?.name || '';
    }

    return getCaseDataSourceName(details.displayText, name, aggregationName);
}

const getOperandButton = (
    operand: Operand,
    tooltip: string | null,
    functions: UserDefinedFunction[],
    globalFunctions: UserDefinedFunction[],
    lookups: Lookup[],
    dictionaries: Dictionary[],
    variables: ReportVariable[],
    dataSources: IngestionConfig[],
    reportFields: ReportFieldDefinition[],
    customDataSourceFields: CustomDataSourceFields,
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    disabled: boolean,
    reportConfigType: string,
    location?: ReportLogicLocation,
    cases?: ReportCase[],
    aggregations?: Aggregation[],
    selectorNumber?: number,
    children?: JSX.Element,
    operandNumber?: number,
    argumentIndexes?: number[]
): JSX.Element => {

    let details = operandDisplayHelper.getOperandDetails(operand, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields, reportConfigType, null, cases, location as ReportLogicLocation);

    if (operand.operandType === operandType.field ||
        operand.operandType === operandType.variable) {

        if ((location?.statement === locationType.statement.case || location?.statement === locationType.statement.reportField) &&
            cases && aggregations) {

            let aggregationNumber = cases.find(x => x.number === location.statementNumber)?.aggregationNumber;

            if (aggregationNumber) {

                let aggregation = aggregations.find(x => x.number === aggregationNumber);
                details.displayText = getDisplayName(operand, aggregation?.name || '', dataSources, variables, details);
            }
        }

        if ((location?.statement === locationType.statement.aggregationFilter) && aggregations) {

            let aggregation = aggregations?.find(x => x.number === location.parentNumber);
            details.displayText = getDisplayName(operand, aggregation?.name || '', dataSources, variables, details);
        }
    }

    const onButtonMoreArgumentsClick = (lookup: Lookup) => {

        let lookupLocation = location ?
            { ...location, argumentIndexes: [...(argumentIndexes || [])] } as ReportLogicLocation :
            location;

        store.dispatch({
            type: actions.indexedLookupArgumentsModal.open, payload: {
                argumentIndexes: argumentIndexes || [],
                lookup,
                customDataSourceFields: customDataSourceFields,
                variables,
                lookups,
                dataSources,
                dictionaries,
                functions,
                globalFunctions,
                disabled,
                onClick: (number, argumentIndexes, e) => onClick(e),
                operand,
                operandNumber,
                reportConfigType,
                reportFields,
                selectorNumber,
                aggregations,
                cases,
                location: lookupLocation
            } as IndexedLookupArgumentsState
        });
    }

    let isLookup = operand.operandType === operandType.lookupField;

    let showButtonJoinForIndexedLookup = false;

    let lookup: Lookup;

    if (isLookup) {

        lookup = lookups.find(x => x.number === operand.lookupNumber) as Lookup;

        if (lookup.method === LookupMethod.Indexed) {

            showButtonJoinForIndexedLookup = true;
        }
    }

    return (
        <span className='display-flex align-items-center'>
            <button className={getOperandButtonClasses(details)} onClick={(е) => {
                onClick(е);
            }} title={tooltip as string} disabled={disabled}>
                {getOperandButtonText(details)}
            </button>
            {
                typeHelper.isString(details.definitionHtmlId) &&
                <div>
                    <button className="transparent-button" onClick={() => onShowDefinitionClick(details, location || {} as ReportLogicLocation)} title="Go to definition">*</button>
                    {showButtonJoinForIndexedLookup &&
                        <button className='transparent-button code function' onClick={() => onButtonMoreArgumentsClick(lookup)}>[JOIN]</button>
                    }
                </div>
            }
            {children}
        </span >
    );
};

const getOperandContent = (
    operand: Operand,
    operandNumber: number,
    argumentIndexes: number[],
    tooltip: string | null,
    functions: UserDefinedFunction[],
    globalFunctions: UserDefinedFunction[],
    lookups: Lookup[],
    dictionaries: Dictionary[],
    variables: ReportVariable[],
    dataSources: IngestionConfig[],
    reportFields: ReportFieldDefinition[],
    customDataSourceFields: CustomDataSourceFields,
    selectorNumber: number | null,
    onClick: (operandNumber: number, argumentIndexes: number[], event: React.MouseEvent<HTMLElement>) => void,
    disabled: boolean,
    reportConfigType: string,
    location?: ReportLogicLocation,
    cases?: ReportCase[],
    aggregations?: Aggregation[],
    children?: JSX.Element | undefined
): JSX.Element => {

    if (operand.operandType === operandType.function) {

        return <FunctionOperand
            operand={operand}
            operandNumber={operandNumber}
            argumentIndexes={argumentIndexes}
            functions={functions}
            globalFunctions={globalFunctions}
            lookups={lookups}
            dictionaries={dictionaries}
            variables={variables}
            dataSources={dataSources}
            reportFields={reportFields}
            customDataSourceFields={customDataSourceFields}
            selectorNumber={selectorNumber}
            onClick={(operandNumber: number, argumentIndexes: number[], event: React.MouseEvent<HTMLElement>) => { onClick(operandNumber, argumentIndexes, event) }}
            disabled={disabled}
            reportConfigType={reportConfigType}
            location={location}
            cases={cases}
            aggregations={aggregations}
            children={children}
        />
    }

    if (operand.operandType === operandType.dictionary ||
        operand.operandType === operandType.dictionaryResultSet) {

        return getDictionaryOperand(operand, operandNumber, argumentIndexes, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields,
            selectorNumber, (number, argIndexes, event) => { onClick(number, argIndexes, event) }, disabled, reportConfigType, children);
    }

    if (operand.operandType === operandType.userDefinedFunction || operand.operandType === operandType.globalUserDefinedFunction) {

        return getUserDefinedFunctionOperand(operand, operandNumber, argumentIndexes, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields,
            selectorNumber, (number, argIndexes, event) => { onClick(number, argIndexes, event) }, disabled, reportConfigType, children);
    }

    return getOperandButton(operand, tooltip, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields,
        (event) => { onClick(operandNumber, argumentIndexes, event) }, disabled, reportConfigType, location, cases, aggregations, selectorNumber as number, children, operandNumber, argumentIndexes);
};


const getCaseDataSourceName = (displayText: string, name: string, aggregationName: string): string => {

    let text = mixedOperandHelper.buildAggregatedCaseWithFieldIdentifier(aggregationName, name);

    return text || displayText;
}


export {
    getOperandContent
};
